// Customer Excel workbooks.
//
// Two files, both built here so the column meanings live in one place:
//   • everyone   — one row per customer for the chosen period, plus who owes
//                  money and who buys the most
//   • one person — their profile, every inquiry, every part, returns, follow-ups
//
// Every money sheet carries BOTH pictures, because they answer different
// questions: the selected period (what they did lately) and lifetime (what
// they are worth to us). Column names say which is which — no bare "Sold".

import * as XLSX from 'xlsx';

// Sheets size their own columns — a report nobody has to resize to read.
const aoa = (rows) => {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const widths = [];
  rows.forEach((r) => (r || []).forEach((cell, i) => {
    const len = String(cell ?? '').length;
    if (!widths[i] || len > widths[i]) widths[i] = len;
  }));
  ws['!cols'] = widths.map((w) => ({ wch: Math.max(6, Math.min(42, (w || 0) + 2)) }));
  return ws;
};
const n = (v) => Number(v || 0);

export const PERIOD_LABEL = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'This week',
  month: 'This month',
  year: 'This year',
  all: 'All time',
  custom: 'Date range',
};

// What the sheet headers call the window — a custom range names its dates.
export const windowLabel = (report, period) => {
  if (period === 'custom') {
    const w = report?.window || {};
    return `${w.from || 'the beginning'} → ${w.to || ''}`;
  }
  return PERIOD_LABEL[period] || period;
};

const windowSlug = (report, period) => {
  if (period !== 'custom') return period;
  const w = report?.window || {};
  return `${w.from || 'start'}_to_${w.to || 'now'}`;
};

const save = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  sheets.forEach(([name, ws]) => XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31)));
  XLSX.writeFile(wb, filename);
};

const safeName = (s) => (s || 'customer').replace(/[^\w]+/g, '_').replace(/^_|_$/g, '');

// ─── Everyone ────────────────────────────────────────────────────────────────

const CUSTOMER_HEADER = [
  'Customer', 'Phone', 'Location', 'Added by', 'Joined',
  'Inquiries', 'Pending', 'Live', 'Waiting', 'On hold', 'Sold', 'Lost',
  'Deals sold', 'Sold value', 'Given to company', 'Balance owed', 'Refunded',
  'Last sale', 'Served by', 'Cars asked about',
  'LIFETIME inquiries', 'LIFETIME deals', 'LIFETIME sold value',
  'LIFETIME given', 'LIFETIME balance', 'First ever inquiry', 'Last ever inquiry',
];

const customerRow = (c) => [
  c.name, c.phone, c.location, c.added_by, c.joined,
  c.inquiries, c.stages?.pending ?? 0, c.stages?.live ?? 0, c.stages?.waiting ?? 0,
  c.stages?.hold ?? 0, c.stages?.won ?? 0, c.stages?.loss ?? 0,
  c.sold_deals, n(c.sold_value), n(c.collected), n(c.balance), n(c.refunded),
  c.last_sold_at || '', (c.salespersons || []).join(', '), (c.cars || []).join(', '),
  c.lifetime?.inquiries ?? 0, c.lifetime?.sold_deals ?? 0, n(c.lifetime?.sold_value),
  n(c.lifetime?.collected), n(c.lifetime?.balance),
  c.lifetime?.first_inquiry || '', c.lifetime?.last_inquiry || '',
];

const totalRow = (rows) => {
  const sum = (f) => rows.reduce((a, c) => a + n(f(c)), 0);
  return [
    'TOTAL', '', '', '', '',
    sum((c) => c.inquiries),
    sum((c) => c.stages?.pending), sum((c) => c.stages?.live), sum((c) => c.stages?.waiting),
    sum((c) => c.stages?.hold), sum((c) => c.stages?.won), sum((c) => c.stages?.loss),
    sum((c) => c.sold_deals), sum((c) => c.sold_value), sum((c) => c.collected),
    sum((c) => c.balance), sum((c) => c.refunded), '', '', '',
    sum((c) => c.lifetime?.inquiries), sum((c) => c.lifetime?.sold_deals),
    sum((c) => c.lifetime?.sold_value), sum((c) => c.lifetime?.collected),
    sum((c) => c.lifetime?.balance), '', '',
  ];
};

/** The whole customer base for one period. */
export function downloadCustomersWorkbook(report, period) {
  const rows = report?.customers || [];
  const t = report?.totals || {};
  const label = windowLabel(report, period);

  const summary = ['Summary', aoa([
    ['CUSTOMERS REPORT'],
    ['Period', label],
    ['Window', `${report?.window?.from || 'the beginning'} → ${report?.window?.to || ''}`],
    ['Generated', report?.generated_at || ''],
    [],
    ['— Who they are —'],
    ['Customers in the list', t.customers ?? 0],
    ['New this period', t.new_customers ?? 0],
    ['Raised an inquiry this period', t.active_customers ?? 0],
    ['Actually bought this period', t.buying_customers ?? 0],
    ['Still owe us money', t.owing_customers ?? 0],
    [],
    [`— Money, ${label.toLowerCase()} —`],
    ['Inquiries', t.inquiries ?? 0],
    ['Deals sold', t.sold_deals ?? 0],
    ['Sold value (incl. money not yet paid)', n(t.sold_value)],
    ['GIVEN TO COMPANY (collected)', n(t.collected)],
    ['Balance still with customers', n(t.balance)],
    ['Refunded back', n(t.refunded)],
    [],
    ['— Lifetime, every customer in this list —'],
    ['Sold value, all time', n(t.lifetime_sold_value)],
    ['Given to company, all time', n(t.lifetime_collected)],
  ])];

  const all = ['Customers', aoa([
    [`Customers — ${label}`],
    ['Plain columns are the selected period. LIFETIME columns are everything they have ever done.'],
    [],
    CUSTOMER_HEADER,
    ...rows.map(customerRow),
    [],
    totalRow(rows),
  ])];

  const owing = rows.filter((c) => n(c.balance) > 0)
    .sort((a, b) => n(b.balance) - n(a.balance));
  const owingSheet = ['Who owes money', aoa([
    ['Customers with money still to collect — biggest first'],
    [],
    ['Customer', 'Phone', 'Location', 'Balance owed', 'Unpaid deals',
      'Sold value', 'Given to company', 'Last sale', 'Served by'],
    ...owing.map((c) => [c.name, c.phone, c.location, n(c.balance), c.due_count ?? 0,
      n(c.sold_value), n(c.collected), c.last_sold_at || '',
      (c.salespersons || []).join(', ')]),
    [],
    ['TOTAL', '', '', owing.reduce((a, c) => a + n(c.balance), 0)],
  ])];

  const buyers = rows.filter((c) => n(c.sold_value) > 0)
    .sort((a, b) => n(b.sold_value) - n(a.sold_value));
  const buyersSheet = ['Top buyers', aoa([
    [`Best customers — ${label}, by what they bought`],
    [],
    ['#', 'Customer', 'Phone', 'Deals sold', 'Sold value', 'Given to company',
      'Balance owed', 'LIFETIME sold value'],
    ...buyers.map((c, i) => [i + 1, c.name, c.phone, c.sold_deals, n(c.sold_value),
      n(c.collected), n(c.balance), n(c.lifetime?.sold_value)]),
  ])];

  // The full story of the window — every inquiry with its car, every part
  // with its price. This is what "download the data" actually means.
  const inquiriesSheet = ['Inquiries', aoa([
    [`Every inquiry — ${label}`],
    [],
    ['Inquiry #', 'Date', 'Customer', 'Phone', 'Stage', 'Car', 'Parts',
      'Salesperson', 'Quoted by', 'First quote', 'Final price', 'Payment type',
      'Paid by', 'Advance', 'Final amount', 'Balance settled',
      'Payment confirmed', 'Dispatched', 'Delivery address',
      'Sales notes', 'Supervisor notes'],
    ...(report?.inquiries || []).map((r) => [
      r.inquiry_id, r.created_at, r.customer, r.customer_phone, r.stage, r.car,
      r.parts, r.salesperson, r.quoted_by, r.original_quoted_price,
      r.quoted_price, r.payment_type, r.payment_mode, r.advance_amount,
      r.final_amount, r.balance_paid ? 'Yes' : '', r.payment_confirmed_at,
      r.dispatched_at, r.dispatch_address, r.notes, r.supervisor_notes]),
  ])];

  const partsSheet = ['Parts', aoa([
    [`Every part asked for — ${label}`],
    [],
    ['Inquiry #', 'Date', 'Customer', 'Phone', 'Car', 'Part', 'Qty',
      'Source', 'Price', 'Stage'],
    ...(report?.parts || []).map((r) => [r.inquiry_id, r.date, r.customer,
      r.customer_phone, r.car, r.part, r.quantity, r.source, r.price, r.stage]),
  ])];

  save([summary, all, inquiriesSheet, partsSheet, owingSheet, buyersSheet],
       `customers_${windowSlug(report, period)}.xlsx`);
}

// ─── One customer ────────────────────────────────────────────────────────────

/** Everything on file for a single customer. */
export function downloadCustomerWorkbook(report, period) {
  const c = report?.customer || {};
  const s = report?.summary || {};
  const label = windowLabel(report, period);
  const p = s.period || {};
  const l = s.lifetime || {};

  const profile = ['Profile', aoa([
    ['CUSTOMER FILE'],
    ['Name', c.name || ''],
    ['Phone', c.phone || ''],
    ['Location', c.location || ''],
    ['Added by', c.added_by || ''],
    ['Customer since', c.joined || ''],
    ['Generated', report?.generated_at || ''],
    [],
    [`— ${label} —`],
    ['Inquiries', p.inquiries ?? 0],
    ['Pending / Live / Waiting', `${p.stages?.pending ?? 0} / ${p.stages?.live ?? 0} / ${p.stages?.waiting ?? 0}`],
    ['On hold / Sold / Lost', `${p.stages?.hold ?? 0} / ${p.stages?.won ?? 0} / ${p.stages?.loss ?? 0}`],
    ['Deals sold', p.sold_deals ?? 0],
    ['Sold value', n(p.sold_value)],
    ['Given to company', n(p.collected)],
    ['Balance owed', n(p.balance)],
    ['Refunded', n(p.refunded)],
    [],
    ['— Lifetime —'],
    ['Inquiries', l.inquiries ?? 0],
    ['Pending / Live / Waiting', `${l.stages?.pending ?? 0} / ${l.stages?.live ?? 0} / ${l.stages?.waiting ?? 0}`],
    ['On hold / Sold / Lost', `${l.stages?.hold ?? 0} / ${l.stages?.won ?? 0} / ${l.stages?.loss ?? 0}`],
    ['Deals sold', l.sold_deals ?? 0],
    ['Sold value', n(l.sold_value)],
    ['GIVEN TO COMPANY', n(l.collected)],
    ['Balance still owed', n(l.balance)],
    ['Refunded', n(l.refunded)],
    ['Last purchase', l.last_sold_at || '—'],
  ])];

  const inquiries = ['Inquiries', aoa([
    ['Every inquiry this customer ever raised'],
    [],
    ['Inquiry #', 'Date', 'Stage', 'Car', 'Parts', 'Salesperson', 'Quoted by',
      'First quote', 'Final price', 'Payment type', 'Paid by', 'Advance',
      'Final amount', 'Balance settled', 'Payment confirmed', 'Dispatched',
      'Delivery address', 'Sales notes', 'Supervisor notes'],
    ...(report?.inquiries || []).map((r) => [
      r.inquiry_id, r.created_at, r.stage, r.car, r.parts, r.salesperson, r.quoted_by,
      r.original_quoted_price, r.quoted_price, r.payment_type, r.payment_mode,
      r.advance_amount, r.final_amount, r.balance_paid ? 'Yes' : '',
      r.payment_confirmed_at, r.dispatched_at, r.dispatch_address,
      r.notes, r.supervisor_notes]),
  ])];

  const parts = ['Parts', aoa([
    ['Every part they asked for, and where it came from'],
    [],
    ['Inquiry #', 'Date', 'Car', 'Part', 'Qty', 'Source', 'Price', 'Stage'],
    ...(report?.parts || []).map((r) => [r.inquiry_id, r.date, r.car, r.part,
      r.quantity, r.source, r.price, r.stage]),
  ])];

  const returns = ['Returns', aoa([
    ['Products this customer brought back'],
    [],
    ['Date', 'Inquiry #', 'Product', 'Qty', 'Type', 'Refund', 'Status', 'Cash handed over', 'Reason'],
    ...(report?.returns || []).map((r) => [r.created_at, r.inquiry_id, r.product,
      r.quantity, r.type, r.refund_amount, r.status, r.cash_paid ? 'Yes' : '', r.reason]),
  ])];

  const followUps = ['Follow-ups', aoa([
    ['Every follow-up logged against this customer'],
    [],
    ['Date', 'Inquiry #', 'Outcome', 'Note', 'Next follow-up'],
    ...(report?.follow_ups || []).map((r) => [r.created_at, r.inquiry_id, r.outcome,
      r.note, r.next_follow_up_at]),
  ])];

  save([profile, inquiries, parts, returns, followUps],
       `customer_${safeName(c.name)}_${c.phone || c.id || ''}.xlsx`);
}
